import { ChangeEvent, FC } from "react"
import {
    Checkbox,
    ExclusiveChoiceDropdown,
    SingleLineTextInput
} from "dash-styled"

import { AppTTLSettings } from "../../../../../../../Models/API/AppSettings"
import { Banner } from "../../../../../../Common/Banner/Banner"

interface TTLProps {
    value: AppTTLSettings
    onChange: (value: AppTTLSettings) => void
}

enum TTLOptions {
    MINUTES = "minutes",
    HOURS = "hours",
    DAYS = "days"
}

const MIN_VALUE = 1

const getSettings = (option: TTLOptions) => {
    const minutesMultiplier = 60
    const hoursMultiplier = minutesMultiplier * 60
    const daysMultiplier = hoursMultiplier * 24

    switch (option) {
        case TTLOptions.MINUTES:
            return {
                multiplier: minutesMultiplier,
                maxValue: 59
            }
        case TTLOptions.HOURS:
            return {
                multiplier: hoursMultiplier,
                maxValue: 23
            }
        case TTLOptions.DAYS:
            return {
                multiplier: daysMultiplier,
                maxValue: 30
            }
    }
}

export const TTL: FC<TTLProps> = ({ value: propsValue, onChange }) => {
    const {
        onCompleted: { enabled, ttlInSeconds }
    } = propsValue
    const minutes = Math.max(MIN_VALUE, Math.floor(ttlInSeconds / 60))
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    let value = minutes
    let option: TTLOptions = TTLOptions.MINUTES
    if (days > 0) {
        value = days
        option = TTLOptions.DAYS
    } else if (hours > 0) {
        value = hours
        option = TTLOptions.HOURS
    }

    const { maxValue: MAX_VALUE, multiplier } = getSettings(option)

    const handleOnCompletedChange = (e: ChangeEvent<HTMLInputElement>) => {
        let originalValue = Number.parseInt(e.target.value, 10)
        originalValue = Math.min(MAX_VALUE, originalValue)
        originalValue = Math.max(MIN_VALUE, originalValue)

        onChange({
            ...propsValue,
            onCompleted: {
                ...propsValue.onCompleted,
                ttlInSeconds: originalValue * multiplier
            }
        })
    }

    const handleOnCompletedOptionChange = (option: TTLOptions) => {
        const nextSettings = getSettings(option)
        onChange({
            ...propsValue,
            onCompleted: {
                ...propsValue.onCompleted,
                ttlInSeconds:
                    Math.min(nextSettings.maxValue, value) *
                    nextSettings.multiplier
            }
        })
    }

    const handleOnCompletedToggle = () => {
        onChange({
            ...propsValue,
            onCompleted: {
                ...propsValue.onCompleted,
                enabled: !enabled
            }
        })
    }

    return (
        <>
            <Banner icon="info" className="mt-4">
                <>
                    TTL settings improve security by defining record lifespans
                    and auto-removing sensitive data, reducing unauthorized
                    access risks. However, rapid data removal may impact machine
                    learning model training and performance. Balancing data
                    retention and security is crucial for optimal outcomes.
                </>
            </Banner>
            <ul className="pt-2">
                <li className="flex items-center pb-2">
                    <Checkbox
                        testId="ttl-on-completed-toggle"
                        variant="modern"
                        isChecked={enabled}
                        onChange={handleOnCompletedToggle}
                        className="mr-2"
                        label="Delete data points"
                    />
                    <SingleLineTextInput
                        transparentBg
                        testId="ttl-on-completed-minutes"
                        className="w-20 mx-1"
                        size="sm"
                        type="number"
                        disabled={!enabled}
                        min={MIN_VALUE}
                        max={MAX_VALUE}
                        value={value.toString()}
                        onChange={handleOnCompletedChange}
                    />
                    <ExclusiveChoiceDropdown<TTLOptions>
                        size="sm"
                        testId="ttl-on-completed-option"
                        className="w-48 mr-2"
                        disabled={!enabled}
                        value={option}
                        choices={Object.values(TTLOptions)}
                        onClick={handleOnCompletedOptionChange}
                    />
                    after they are in completed status
                </li>
            </ul>
        </>
    )
}
